import { SingleSymbologyConfig, SymbologyConfig, SymbologyIcon, UniqueCategory, IconType, QuantityClass } from '../../../../types/symbology.types';
import { defaultLayerPropertiesState } from '../../../../context/LayerPropertiesContext';
import { isBuiltInShape } from './components/builtInShapes';
import type { MapLayer } from '../../../../types/CommonMapTypes';

// US-101663 Scenario 6: Display labels are capped at 50 characters.
export const MAX_DISPLAY_LABEL = 50;
export const DISPLAY_LABEL_ERROR = 'Display value cannot exceed 50 characters';
export const UNIQUE_FIELD_REQUIRED_ERROR = 'Select a field to configure unique symbology';

/** Subset of a persisted layer row the symbology resolver reads. */
export interface SymbologyRow {
  symbology?: string | null;
  layerType?: string | null;
  symbologyConfig?: SymbologyConfig | null;
}

/** Subset of LayerPropertiesContext fields the single-symbology adapters read. */
export interface FlatSingleFields {
  fill: string;
  fillType?: string | null;
  fillOpacity: number;
  outline: string;
  outlineType?: string | null;
  outlineOpacity: number;
  outlineWeight: number;
  lineWeight?: number;
  iconType?: string;
  selectedIcon?: string;
  iconSize?: number;
  iconRotation?: number;
  iconUploadKind?: 'shape' | 'image';
  layerType?: string;
}

/** Build a typed SymbologyIcon from the flat context icon fields (or null). */
export const buildIconFromFlat = (state: FlatSingleFields): SymbologyIcon | null => {
  const { selectedIcon, iconType, iconSize, iconRotation, iconUploadKind } = state;
  if (!selectedIcon) return null;
  const isShape = isBuiltInShape(selectedIcon);
  return {
    type: (iconType as IconType) ?? (isShape ? 'shape' : 'styles'),
    key: selectedIcon,
    size: iconSize ?? 0,
    rotation: iconRotation ?? 0,
    uploadKind: iconUploadKind ?? (isShape ? 'shape' : 'image'),
  };
};

/** Build a SingleSymbologyConfig from the flat single-symbology context fields. */
export const buildSingleConfig = (state: FlatSingleFields): SingleSymbologyConfig => ({
  type: 'single',
  fill: state.fill,
  fillOpacity: state.fillOpacity,
  fillType: state.fillType ?? null,
  outline: state.outline,
  outlineOpacity: state.outlineOpacity,
  outlineType: state.outlineType ?? null,
  outlineWeight: state.outlineWeight,
  lineWeight: state.lineWeight ?? 2,
  icon: buildIconFromFlat(state),
  layerType: state.layerType ?? '',
});

/** Inverse of buildSingleConfig. */
export const flatFieldsFromSingleConfig = (config: SingleSymbologyConfig) => ({
  fill: config.fill,
  fillOpacity: config.fillOpacity,
  fillType: config.fillType ?? 'solid',
  outline: config.outline,
  outlineOpacity: config.outlineOpacity,
  outlineType: config.outlineType ?? '',
  outlineWeight: config.outlineWeight,
  lineWeight: config.lineWeight ?? 2,
  ...(config.icon && {
    selectedIcon: config.icon.key,
    iconType: config.icon.type,
    iconSize: config.icon.size,
    iconRotation: config.icon.rotation,
    iconUploadKind: config.icon.uploadKind ?? undefined,
  }),
});

/** A sensible default single config for a layer of the given geometry type. */
export const defaultSingleConfig = (layerType: string): SingleSymbologyConfig => {
  const isPoint = layerType === 'Point' || layerType === 'MultiPoint';
  return {
    type: 'single',
    fill: '#AAAAAA',
    fillOpacity: 100,
    fillType: 'solid',
    outline: '#000000',
    outlineOpacity: 100,
    outlineType: 'solid',
    outlineWeight: 1,
    lineWeight: 2,
    icon: isPoint ? { type: 'shape', key: 'shape:circle', size: 12, rotation: 0, uploadKind: 'shape' } : null,
    layerType,
  };
};

/**
 * The single config a saved row renders with — symbology_config wins over the
 * legacy flat columns. A "start from scratch" duplicate has no `symbology` but
 * still carries a copy of the source's config, so it starts on the default.
 */
export const resolveSingleConfig = (layer: SymbologyRow): SingleSymbologyConfig | null => {
  if (!layer.symbology) return defaultSingleConfig(layer.layerType ?? '');
  return layer.symbologyConfig?.type === 'single' ? layer.symbologyConfig : null;
};

/** What a feature looks like on the map: the colours it is drawn in, and its marker size when it is a point. */
export interface FeatureLook {
  colors: string[];
  pointSize: number | null;
}

const NO_LOOK: FeatureLook = { colors: [], pointSize: null };

// An image icon has no single colour to compare against, so it contributes only its size.
const lookOf = (config: SingleSymbologyConfig): FeatureLook => {
  const { fill, fillOpacity, fillType, outline, outlineOpacity, outlineType, outlineWeight, icon, layerType } = config;
  const isImageIcon = !!icon && !isBuiltInShape(icon.key);
  // A line is drawn by its fill; its outline is only an optional buffer.
  const isLine = !!layerType?.includes('Line');
  const hasFill = !isImageIcon && (isLine || fillType !== 'none') && fillOpacity > 0;
  const hasStroke = !isImageIcon && outlineType !== 'none' && outlineOpacity > 0 && (!isLine || outlineWeight > 0);
  const colors = [...(hasFill ? [fill] : []), ...(hasStroke ? [outline] : [])];
  // A mark's stroke is drawn around its size, so it widens the marker; an image has none.
  const pointSize = icon && icon.size > 0 ? icon.size + (hasStroke ? outlineWeight : 0) : null;
  return { colors, pointSize };
};

// PARITY: mirrors the flat-column fallbacks of applyLayerStyle in src/views/CommonMap/layerStyle.utils.ts — re-check when that flow changes
// A single-symbology row saved before symbology_config existed.
const flatSingleConfig = (layer: MapLayer): SingleSymbologyConfig => {
  const base = defaultSingleConfig(layer.layerType ?? '');
  return {
    ...base,
    fill: layer.fill || base.fill,
    fillOpacity: layer.fillOpacity ?? base.fillOpacity,
    fillType: layer.fillType ?? base.fillType,
    outline: layer.outline || base.outline,
    outlineOpacity: layer.outlineOpacity ?? base.outlineOpacity,
    outlineType: layer.outlineType ?? base.outlineType,
    outlineWeight: layer.outlineWeight ?? base.outlineWeight,
    icon: base.icon && { ...base.icon, key: layer.selectedIcon || base.icon.key, size: layer.iconSize || base.icon.size },
  };
};

const inClass = (value: number, { min, max, minOp, maxOp }: QuantityClass) => {
  const aboveMin = minOp === null || (minOp === '>' ? value > min : value >= min);
  const belowMax = maxOp === null || (maxOp === '<' ? value < max : value <= max);
  return aboveMin && belowMax;
};

const mergeLooks = (looks: FeatureLook[]): FeatureLook => {
  const sizes = looks.flatMap(({ pointSize }) => (pointSize === null ? [] : [pointSize]));
  return { colors: looks.flatMap(({ colors }) => colors), pointSize: sizes.length ? Math.max(...sizes) : null };
};

// Multi-class rows saved before symbology_config carry no per-class colours, only their marker sizes.
const legacySizeLook = (layer: MapLayer): FeatureLook => {
  const sizes = layer.symbology === 'quantity' ? [layer.quantityIconSize] : [layer.iconSize, layer.uniqueIconSize];
  return mergeLooks(sizes.map((size) => ({ colors: [], pointSize: size ? size : null })));
};

/** How the layer draws one feature, from its symbology (single, unique category or quantity class). */
export const resolveFeatureLook = (layer: MapLayer | undefined, attributes: Record<string, unknown>): FeatureLook => {
  if (!layer || layer.symbology === 'heatmap') return NO_LOOK;
  const config = layer.symbologyConfig;
  if (layer.symbology === 'unique' || layer.symbology === 'quantity') {
    if (config?.type === 'unique') {
      const category = config.categories.find(({ value }) => String(value) === String(attributes[config.field]));
      return category ? lookOf(category.symbology) : NO_LOOK;
    }
    if (config?.type === 'quantity') {
      const raw = attributes[config.field];
      // Number(null) and Number('') are 0, which would match the class holding 0.
      const value = raw === null || raw === undefined || raw === '' ? NaN : Number(raw);
      const matched = Number.isFinite(value) ? config.classes.find((quantityClass) => inClass(value, quantityClass)) : undefined;
      return matched ? lookOf(matched.symbology) : NO_LOOK;
    }
    return legacySizeLook(layer);
  }
  if (config && config.type !== 'single') return NO_LOOK;
  return lookOf(resolveSingleConfig(layer) ?? flatSingleConfig(layer));
};

/** How the layer draws any of its features: every colour it uses and its largest marker, so one highlight covers them all. */
export const resolveLayerLook = (layer: MapLayer | undefined): FeatureLook => {
  const config = layer?.symbologyConfig;
  if (layer?.symbology === 'unique' && config?.type === 'unique') return mergeLooks(config.categories.map(({ symbology }) => lookOf(symbology)));
  if (layer?.symbology === 'quantity' && config?.type === 'quantity') return mergeLooks(config.classes.map(({ symbology }) => lookOf(symbology)));
  return resolveFeatureLook(layer, {});
};

/** Seed the Layer Properties context from a saved layer row. */
export const seedLayerProperties = (layer: any): typeof defaultLayerPropertiesState => {
  const config = resolveSingleConfig(layer);
  return {
    ...defaultLayerPropertiesState,
    ...Object.fromEntries(Object.entries(layer).filter(([, v]) => v !== null && v !== undefined)),
    ...(config ? flatFieldsFromSingleConfig(config) : {}),
    symbology: layer.symbology || 'single',
    symbologyConfig: config ?? layer.symbologyConfig,
    isExistingLayer: true,
  };
};

/**
 * The label shown in the legend for a category: the user's display label, or
 * the raw value when the label was left blank (US-101663 Scenario 7).
 */
export const resolveDisplayLabel = (category: UniqueCategory): string =>
  category.displayLabel.trim() === '' ? String(category.value) : category.displayLabel;

/** Build a fresh category for a value, seeded with the supplied default symbology. */
export const makeCategory = (value: string | number, baseSymbology: SingleSymbologyConfig): UniqueCategory => ({
  value,
  displayLabel: '',
  symbology: { ...baseSymbology, icon: baseSymbology.icon ? { ...baseSymbology.icon } : null },
});

/**
 * US-101664 Scenarios 11 & 12 — reconcile a saved unique config against the
 * current dataset values after a re-upload:
 *   • keep categories whose value still exists;
 *   • drop categories whose value no longer exists;
 *   • do NOT auto-add brand-new values (they stay available via Add Category).
 */
export const reconcileCategories = (categories: UniqueCategory[], datasetValues: (string | number)[]): UniqueCategory[] => {
  const present = new Set(datasetValues.map((v) => String(v)));
  return categories.filter((c) => present.has(String(c.value)));
};

/** Dataset values not currently represented by a category (the Add Category pool). */
export const unsymbolizedValues = (categories: UniqueCategory[], datasetValues: (string | number)[]): (string | number)[] => {
  const used = new Set(categories.map((c) => String(c.value)));
  return datasetValues.filter((v) => !used.has(String(v)));
};

// Before the line model, a line was drawn from Outline (colour, opacity, dash, weight) and Fill was unused.
// Saved lines carry no `lineWeight`, so move their look onto Fill / lineWeight and drop the buffer.
const migrateLegacyLine = <T extends SingleSymbologyConfig | (Partial<FlatSingleFields> & { fill?: string })>(look: T): T => {
  const outlineType = (look as { outlineType?: string | null }).outlineType;
  return {
    ...look,
    fill: (look as { outline?: string }).outline || look.fill,
    fillOpacity: (look as { outlineOpacity?: number }).outlineOpacity ?? (look as { fillOpacity?: number }).fillOpacity,
    fillType: outlineType === 'dashed' || outlineType === 'dotted' ? outlineType : 'solid',
    lineWeight: (look as { outlineWeight?: number }).outlineWeight ?? 2,
    outlineType: 'none',
  };
};

/** Brings a saved layer row that predates the line model (Fill = line, Outline = buffer) onto it. */
export const migrateLegacyLineLayer = <T extends SymbologyRow & Partial<FlatSingleFields>>(layer: T): T => {
  if (!layer?.layerType?.includes('Line')) return layer;
  const config = layer.symbologyConfig;
  if (config?.type === 'single') return config.lineWeight === undefined ? { ...layer, symbologyConfig: migrateLegacyLine(config) } : layer;
  if (config?.type === 'unique') {
    if (config.categories.every(({ symbology }) => symbology.lineWeight !== undefined)) return layer;
    return {
      ...layer,
      symbologyConfig: {
        ...config,
        categories: config.categories.map((category) =>
          category.symbology.lineWeight === undefined ? { ...category, symbology: migrateLegacyLine(category.symbology) } : category,
        ),
      },
    };
  }
  // A single-symbology row saved before symbology_config existed carries only the flat columns.
  if (!config && layer.symbology === 'single' && layer.lineWeight === undefined) return migrateLegacyLine(layer);
  return layer;
};
